import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

interface SecurityEvent {
  id: string
  type: 'login' | 'logout' | 'failed_login' | 'password_change' | 'role_change' | 'data_access'
  user: {
    name: string
    email: string
    role: string
  }
  timestamp: string
  ipAddress: string
  userAgent: string
  details: string
  severity: 'low' | 'medium' | 'high' | 'critical'
}

const AdminSecurityAuditPage = () => {
  const navigate = useNavigate()
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [timeFilter, setTimeFilter] = useState<'24h' | '7d' | '30d'>('24h')
  const [typeFilter, setTypeFilter] = useState<'all' | 'login' | 'failed_login' | 'admin'>('all')

  useEffect(() => {
    loadSecurityEvents()
  }, [timeFilter, typeFilter])

  const loadSecurityEvents = async () => {
    setLoading(true)
    try {
      // Simulate API call - in real implementation, fetch from audit logs
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock security events
      const mockEvents: SecurityEvent[] = [
        {
          id: '1',
          type: 'login',
          user: { name: 'John Admin', email: 'john@example.com', role: 'admin' },
          timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          ipAddress: '192.168.1.100',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          details: 'Successful admin login',
          severity: 'low'
        },
        {
          id: '2',
          type: 'failed_login',
          user: { name: 'Unknown', email: 'hacker@malicious.com', role: 'unknown' },
          timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
          ipAddress: '203.0.113.42',
          userAgent: 'curl/7.68.0',
          details: 'Failed login attempt with invalid credentials',
          severity: 'high'
        },
        {
          id: '3',
          type: 'role_change',
          user: { name: 'Super Admin', email: 'super@example.com', role: 'super_admin' },
          timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
          ipAddress: '192.168.1.101',
          userAgent: 'Mozilla/5.0 (macOS; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
          details: 'Changed user role from user to admin for user@example.com',
          severity: 'medium'
        },
        {
          id: '4',
          type: 'data_access',
          user: { name: 'John Admin', email: 'john@example.com', role: 'admin' },
          timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          ipAddress: '192.168.1.100',
          userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          details: 'Accessed user management panel',
          severity: 'low'
        }
      ]
      
      setSecurityEvents(mockEvents)
    } catch (error) {
      console.error('Error loading security events:', error)
    } finally {
      setLoading(false)
    }
  }

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'login': return UserIcon
      case 'logout': return UserIcon
      case 'failed_login': return ExclamationTriangleIcon
      case 'password_change': return LockClosedIcon
      case 'role_change': return ShieldCheckIcon
      case 'data_access': return EyeIcon
      default: return DocumentTextIcon
    }
  }

  const getEventColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30'
      case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/30'
      case 'medium': return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30'
      case 'low': return 'text-green-400 bg-green-500/20 border-green-500/30'
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30'
    }
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'login': return 'text-green-400 bg-green-500/20'
      case 'logout': return 'text-blue-400 bg-blue-500/20'
      case 'failed_login': return 'text-red-400 bg-red-500/20'
      case 'password_change': return 'text-purple-400 bg-purple-500/20'
      case 'role_change': return 'text-orange-400 bg-orange-500/20'
      case 'data_access': return 'text-cyan-400 bg-cyan-500/20'
      default: return 'text-gray-400 bg-gray-500/20'
    }
  }

  const filteredEvents = securityEvents.filter(event => {
    const matchesType = typeFilter === 'all' || 
                       (typeFilter === 'admin' && ['role_change', 'data_access'].includes(event.type)) ||
                       event.type === typeFilter
    return matchesType
  })

  const securityStats = {
    totalEvents: securityEvents.length,
    failedLogins: securityEvents.filter(e => e.type === 'failed_login').length,
    adminActions: securityEvents.filter(e => ['role_change', 'data_access'].includes(e.type)).length,
    criticalEvents: securityEvents.filter(e => e.severity === 'critical').length
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Security & Audit" subtitle="Monitor security events and audit trails">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Security Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <DocumentTextIcon className="w-8 h-8 text-blue-400" />
              <span className="text-2xl font-bold text-white">{securityStats.totalEvents}</span>
            </div>
            <div className="text-gray-400 text-sm">Total Events</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
              <span className="text-2xl font-bold text-white">{securityStats.failedLogins}</span>
            </div>
            <div className="text-gray-400 text-sm">Failed Logins</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <ShieldCheckIcon className="w-8 h-8 text-purple-400" />
              <span className="text-2xl font-bold text-white">{securityStats.adminActions}</span>
            </div>
            <div className="text-gray-400 text-sm">Admin Actions</div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-orange-400" />
              <span className="text-2xl font-bold text-white">{securityStats.criticalEvents}</span>
            </div>
            <div className="text-gray-400 text-sm">Critical Events</div>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-mockup rounded-2xl p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-2">Security Events</h2>
              <p className="text-gray-400">Monitor and audit security-related activities</p>
            </div>

            <div className="flex gap-4">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value as any)}
                className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              >
                <option value="24h" className="bg-gray-800">Last 24 Hours</option>
                <option value="7d" className="bg-gray-800">Last 7 Days</option>
                <option value="30d" className="bg-gray-800">Last 30 Days</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as any)}
                className="px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
              >
                <option value="all" className="bg-gray-800">All Events</option>
                <option value="login" className="bg-gray-800">Logins</option>
                <option value="failed_login" className="bg-gray-800">Failed Logins</option>
                <option value="admin" className="bg-gray-800">Admin Actions</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Security Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-2xl overflow-hidden"
        >
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-400">Loading security events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-8 text-center">
              <ShieldCheckIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400">No security events found</p>
            </div>
          ) : (
            <div className="p-6">
              <div className="space-y-4">
                {filteredEvents.map((event, index) => {
                  const EventIcon = getEventIcon(event.type)
                  return (
                    <motion.div
                      key={event.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-4 rounded-xl border ${getEventColor(event.severity)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-gray-500 to-slate-500 flex items-center justify-center">
                            <EventIcon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(event.type)}`}>
                                {event.type.replace('_', ' ')}
                              </span>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventColor(event.severity).split(' ')[0]} ${getEventColor(event.severity).split(' ')[1]}`}>
                                {event.severity}
                              </span>
                            </div>
                            <h3 className="text-white font-medium mb-1">{event.details}</h3>
                            <div className="text-gray-400 text-sm space-y-1">
                              <div>User: {event.user.name} ({event.user.email}) - {event.user.role}</div>
                              <div>IP: {event.ipAddress}</div>
                              <div>User Agent: {event.userAgent.substring(0, 60)}...</div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-400 text-sm flex items-center">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            {new Date(event.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>
          )}
        </motion.div>

        {/* Security Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Security Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Two-Factor Authentication</div>
                  <div className="text-gray-400 text-sm">Require 2FA for admin accounts</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" defaultChecked className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">Login Monitoring</div>
                  <div className="text-gray-400 text-sm">Monitor and log all login attempts</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" defaultChecked className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">IP Whitelist</div>
                  <div className="text-gray-400 text-sm">Restrict admin access to specific IPs</div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="glass-mockup rounded-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-4">Audit Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="text-white font-medium block mb-2">Log Retention Period</label>
                <select className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all">
                  <option value="30" className="bg-gray-800">30 days</option>
                  <option value="90" className="bg-gray-800">90 days</option>
                  <option value="365" className="bg-gray-800">1 year</option>
                </select>
              </div>

              <div>
                <label className="text-white font-medium block mb-2">Alert Threshold</label>
                <select className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all">
                  <option value="5" className="bg-gray-800">5 failed attempts</option>
                  <option value="10" className="bg-gray-800">10 failed attempts</option>
                  <option value="20" className="bg-gray-800">20 failed attempts</option>
                </select>
              </div>

              <button className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all">
                Export Audit Log
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  )
}

export default AdminSecurityAuditPage 